/**
 * QuickStatsPanel Component
 *
 * Compact statistics panel for the redesigned Live Activity Dashboard.
 * Displays real-time KPIs in a visually appealing format.
 *
 * Features:
 * - Current rack display (prominent)
 * - Key statistics in compact grid
 * - Trend indicators
 * - Professional design for demo
 *
 * @author BizClick WMS
 * @version 2.0 - Compact redesign
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Activity,
  Users,
  MapPin,
  Clock,
  TrendingUp,
  TrendingDown,
  Minus,
  Package,
  Zap
} from 'lucide-react';
import { LocationActivityEvent } from '../../../../types/locationActivity';
import { QueueStats } from './types';
import { useTranslation } from '../../../../contexts/I18nProvider';

interface QuickStatsPanelProps {
  /** All cached events for calculations */
  allEvents: LocationActivityEvent[];

  /** Current queue statistics */
  queueStats: QueueStats | null;

  /** Currently displayed rack number */
  currentRack: string | null;

  /** Current warehouse ID */
  currentWarehouseId: string | null;

  /** Update interval in milliseconds (default: 1000ms) */
  updateInterval?: number;
}

interface StatItem {
  label: string;
  value: string | number;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  color: string;
  bgColor: string;
  trend?: 'up' | 'down' | 'stable';
}

/**
 * Calculate trend for a metric
 */
const calculateTrend = (current: number, previous: number): 'up' | 'down' | 'stable' => {
  const threshold = 0.05;
  const change = (current - previous) / (previous || 1);

  if (change > threshold) return 'up';
  if (change < -threshold) return 'down';
  return 'stable';
};

/**
 * Get trend icon and color
 */
const getTrendDisplay = (trend: 'up' | 'down' | 'stable') => {
  switch (trend) {
    case 'up':
      return { Icon: TrendingUp, color: 'text-green-500' };
    case 'down':
      return { Icon: TrendingDown, color: 'text-red-500' };
    default:
      return { Icon: Minus, color: 'text-gray-400' };
  }
};

export const QuickStatsPanel: React.FC<QuickStatsPanelProps> = ({
  allEvents,
  queueStats,
  currentRack,
  currentWarehouseId,
  updateInterval = 1000
}) => {
  const { t } = useTranslation();
  const [previousStats, setPreviousStats] = useState<Map<string, number>>(new Map());
  const [lastUpdateTime, setLastUpdateTime] = useState<Date>(new Date());

  /**
   * Calculate statistics
   */
  const stats = useMemo((): StatItem[] => {
    const now = Date.now();
    const fiveMinutesAgo = now - 5 * 60 * 1000;

    // Events in last 5 minutes
    const recentEvents = allEvents.filter(event => {
      const eventTime = new Date(event.timestamp).getTime();
      return eventTime >= fiveMinutesAgo;
    });

    // Active operators
    const activeOperators = new Set(recentEvents.map(e => e.operatorId)).size;

    // Active locations
    const activeLocations = new Set(recentEvents.map(e => e.binCode)).size;

    // Queue info
    const racksInQueue = queueStats?.queueLength || 0;

    // Calculate trends using previous stats ref
    const prevEvents = previousStats.get('eventsLast5Min') || 0;
    const prevOperators = previousStats.get('activeOperators') || 0;
    const prevLocations = previousStats.get('activeLocations') || 0;
    const prevQueue = previousStats.get('racksInQueue') || 0;

    return [
      {
        label: t('manager.modules.rackBuilderLive.quickStats.events5Min', {}, 'Hendelser (5 min)'),
        value: recentEvents.length,
        icon: Activity,
        color: 'text-orange-600',
        bgColor: 'bg-orange-100',
        trend: calculateTrend(recentEvents.length, prevEvents)
      },
      {
        label: t('manager.modules.rackBuilderLive.quickStats.activeOperators', {}, 'Aktive operatører'),
        value: activeOperators,
        icon: Users,
        color: 'text-green-600',
        bgColor: 'bg-green-100',
        trend: calculateTrend(activeOperators, prevOperators)
      },
      {
        label: t('manager.modules.rackBuilderLive.quickStats.activeLocations', {}, 'Aktive lokasjoner'),
        value: activeLocations,
        icon: MapPin,
        color: 'text-blue-600',
        bgColor: 'bg-blue-100',
        trend: calculateTrend(activeLocations, prevLocations)
      },
      {
        label: t('manager.modules.rackBuilderLive.quickStats.racksInQueue', {}, 'Reoler i kø'),
        value: racksInQueue,
        icon: Package,
        color: 'text-purple-600',
        bgColor: 'bg-purple-100',
        trend: calculateTrend(racksInQueue, prevQueue)
      }
    ];
  }, [allEvents, queueStats, previousStats, t]);

  /**
   * Update previous stats periodically (separate from useMemo)
   */
  useEffect(() => {
    const now = Date.now();
    const fiveMinutesAgo = now - 5 * 60 * 1000;

    const recentEvents = allEvents.filter(event => {
      const eventTime = new Date(event.timestamp).getTime();
      return eventTime >= fiveMinutesAgo;
    });

    const activeOperators = new Set(recentEvents.map(e => e.operatorId)).size;
    const activeLocations = new Set(recentEvents.map(e => e.binCode)).size;
    const racksInQueue = queueStats?.queueLength || 0;

    // Update previous stats for next render's trend calculation
    const timer = setTimeout(() => {
      setPreviousStats(new Map([
        ['eventsLast5Min', recentEvents.length],
        ['activeOperators', activeOperators],
        ['activeLocations', activeLocations],
        ['racksInQueue', racksInQueue]
      ]));
    }, updateInterval);

    return () => clearTimeout(timer);
  }, [allEvents, queueStats, updateInterval]);

  /**
   * Update last update time periodically
   */
  useEffect(() => {
    const interval = setInterval(() => {
      setLastUpdateTime(new Date());
    }, updateInterval);

    return () => clearInterval(interval);
  }, [updateInterval]);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-2 bg-gradient-to-r from-orange-50 to-transparent border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-orange-500" />
            <h4 className="text-xs font-bold text-gray-600 uppercase tracking-wide">
              {t('manager.modules.rackBuilderLive.quickStats.title', {}, 'Sanntidsstatistikk')}
            </h4>
          </div>
          <div className="flex items-center gap-1 text-[10px] text-gray-400">
            <Clock className="w-3 h-3" />
            <span>{lastUpdateTime.toLocaleTimeString('nb-NO')}</span>
          </div>
        </div>
      </div>

      {/* Current Rack - Prominent */}
      {currentRack && (
        <div className="mx-3 mt-3 p-3 rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 text-white shadow-lg shadow-orange-500/25">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs font-medium text-orange-100 uppercase tracking-wide">
                {t('manager.modules.rackBuilderLive.quickStats.showingRack', {}, 'Viser reol')}
              </div>
              <div className="text-2xl font-bold tracking-tight">
                {currentRack}
              </div>
            </div>
            {currentWarehouseId && (
              <div className="text-right">
                <div className="text-xs font-medium text-orange-100 uppercase tracking-wide">
                  {t('warehouse.general.warehouse', {}, 'Lager')}
                </div>
                <div className="text-sm font-semibold">
                  {currentWarehouseId}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="p-3 grid grid-cols-2 gap-2">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          const trendDisplay = stat.trend ? getTrendDisplay(stat.trend) : null;
          const TrendIcon = trendDisplay?.Icon;

          return (
            <div
              key={index}
              className="p-3 rounded-lg bg-gray-50 border border-gray-100 hover:border-gray-200 transition-colors"
            >
              <div className="flex items-start justify-between mb-1">
                <div className={`p-1.5 rounded-md ${stat.bgColor}`}>
                  <Icon className={`w-3.5 h-3.5 ${stat.color}`} />
                </div>
                {TrendIcon && stat.trend !== 'stable' && (
                  <TrendIcon className={`w-3.5 h-3.5 ${trendDisplay.color}`} />
                )}
              </div>
              <div className="text-xl font-bold text-gray-900">{stat.value}</div>
              <div className="text-[10px] text-gray-500 font-medium uppercase tracking-wide">
                {stat.label}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer Info */}
      <div className="px-4 py-2 bg-gray-50 border-t border-gray-100">
        <p className="text-[10px] text-gray-400 text-center">
          {t('manager.modules.rackBuilderLive.quickStats.autoUpdateFooter', {}, 'Statistikken oppdateres automatisk hvert sekund')}
        </p>
      </div>
    </div>
  );
};

export default QuickStatsPanel;
